import React from 'react';
import classnames from 'classnames';
import { useIntl } from 'umi';
import SelectMultiple from '@intelligence/v3/components/FormComponents/SelectMultiple';
import { getOptionLocaleText, getPreviewTitle, renderOptionSummary } from '../previewHelpers';
import { getStringArrayValue } from '../shared/valueUtils';
import { FILTER_TYPE } from '../filterTypes';

import type { IFilterRenderProps, IFilterTypeEntry } from '@intelligence/v3/filterCore/registry';
import type { IFilterControlModule } from '../controlModule';
import type { IMultipleSelectFilterValue } from './types';

const formatButtonName = (formatMessage: Function, title?: string, label?: string) => {
  let buttonName = title || label;

  if (typeof title === 'string' && title.startsWith('dbi_')) {
    buttonName = formatMessage({ id: title });
  }

  return buttonName;
};

const formatOptions = (locale: string, options?: any) => {
  let resolvedOptions: any[] = [];

  if (Array.isArray(options) && options.length > 0) {
    resolvedOptions = options.map((option) => {
      const text = getOptionLocaleText(option, locale);

      return {
        ...option,
        text,
      };
    });
  }

  return resolvedOptions;
};

const MultipleSelectRenderer: React.FC<IFilterRenderProps<IMultipleSelectFilterValue>> = (props) => {
  const {
    className,
    field,
    label = '',
    title,
    num = 2,
    isShowButtonLabel = true,
    hasAllCheck = true,
    searchable = true,
    options = [],
    value,
    onChange,
    ...rest
  } = props;
  const { locale, formatMessage } = useIntl();

  return (
    // @ts-ignore
    <SelectMultiple
      {...rest}
      key={field}
      className={classnames('select-item', className)}
      maxSelectionTextCount={num}
      label={formatButtonName(formatMessage, title, label)}
      isShowLabel={isShowButtonLabel}
      showAllOption={hasAllCheck}
      searchable={searchable}
      options={formatOptions(locale, options)}
      value={getStringArrayValue(value)}
      onChange={(payload: string[]) => {
        onChange(payload);
      }}
    />
  );
};

const MultipleSelectPreview: React.FC<{
  item: Parameters<NonNullable<IFilterTypeEntry['renderPreview']>>[0];
  valueItem: any;
}> = (props) => {
  const { item, valueItem } = props;
  const { locale, formatMessage } = useIntl();
  const options = item?.controlProps?.options || [];

  return (
    <>
      <span className="model-form-text">{getPreviewTitle(item, formatMessage)}</span>
      <span className="model-form-context">{renderOptionSummary(valueItem, options, formatMessage, locale)}</span>
    </>
  );
};

const getRenderPreview = (): NonNullable<IFilterTypeEntry['renderPreview']> => {
  function renderMultipleSelectPreview(
    item: Parameters<NonNullable<IFilterTypeEntry['renderPreview']>>[0],
    value: any,
  ): React.ReactNode {
    return <MultipleSelectPreview item={item} valueItem={value} />;
  }

  return renderMultipleSelectPreview;
};

export const multipleSelectControl: IFilterControlModule = {
  type: FILTER_TYPE.MULTIPLE_SELECT,
  entry: {
    render: (props) => {
      return <MultipleSelectRenderer {...props} />;
    },
  },
  getRenderPreview,
};
